import type { JSX } from 'react';
import { LocationField } from '../../core/LocationField';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiError, projectDataApi, type Contact, type ContactInput, type ProjectDetails, undoPaths } from '../../api';
import { formatDate, formatEuro } from '../../format';
import { ListEditor } from '../../ui/ListEditor';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';
import { Section } from '../../ui/Section';
import { useContextMenu } from '../../ui/ContextMenu';

const MARKERS = [
  { value: 'A', label: '[A] Annahme' },
  { value: '!', label: '[!] Achtung' },
  { value: '?', label: '[?] zu klären' },
];

/**
 * Master data of a project (module 2, Projektstamm).
 *
 * Arranged as the offer is read: what the project is and who is involved on
 * the left, the lists that qualify it - assumptions, risks, week grid - on the
 * right. On a phone everything stacks in the same order.
 */
export function ProjectDataPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const undo = useUndo();
  const data = useQuery({ queryKey: ['projectdata', projectId], queryFn: () => projectDataApi.get(projectId) });
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('projectdata.write') ?? false;

  const update = useMutation({
    // One after the other, each with the version the previous one left: the
    // lists are saved whole, and a change by someone else in between must be
    // refused (409) rather than overwritten - but never one's own.
    scope: { id: `projectdata-details-${projectId}` },
    mutationFn: (body: Partial<ProjectDetails>) => {
      const before = queryClient.getQueryData<{ details: ProjectDetails }>(['projectdata', projectId])?.details;
      // The undo carries no version: it is guarded by the values it expects, not by the whole record.
      const step = before
        ? guardedStep('Projektdaten', 'PATCH', undoPaths.projectDetails(projectId), inversePatch(body, before as unknown as Record<string, unknown>), body)
        : null;
      return undo.recordAfter(projectDataApi.updateDetails(projectId, { ...body, version: before?.version }), step);
    },
    onSuccess: (details) =>
      queryClient.setQueryData(['projectdata', projectId], (old: { details: ProjectDetails; contacts: Contact[] } | undefined) =>
        old ? { ...old, details } : old,
      ),
    // After a refusal, show what is stored now.
    onError: () => queryClient.invalidateQueries({ queryKey: ['projectdata', projectId] }),
  });

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Projektdaten konnten nicht geladen werden.</Alert>;
  const { details, contacts } = data.data;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        {update.isError && (
          <Alert color="red" withCloseButton onClose={() => update.reset()}>
            {update.error instanceof ApiError ? update.error.message : 'Speichern fehlgeschlagen.'}
          </Alert>
        )}
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" style={{ alignItems: 'start' }}>
          <Stack gap="md">
            <MasterData projectId={projectId} details={details} canEdit={canEdit} saving={update.isPending} onSave={(body) => update.mutate(body)} />
            <Contacts projectId={projectId} contacts={contacts} canEdit={canEdit} />
            <Section title="Weitere Angaben" description="Was sonst zum Auftrag gehört: Grundlage, Stundenbudget, Zahlungsplan …">
              <ListEditor
                items={details.facts}
                readOnly={!canEdit}
                saving={update.isPending}
                empty={() => ({ label: '', value: '' })}
                fields={[
                  { key: 'label', label: 'Angabe', width: 170, required: true },
                  { key: 'value', label: 'Wert', type: 'longtext', grow: 1 },
                ]}
                onSave={(facts) => update.mutate({ facts })}
              />
            </Section>
          </Stack>
          <Stack gap="md">
            <Section title="Planungsannahmen" description="[A] Annahme · [!] Achtung · [?] noch zu klären">
              <ListEditor
                items={details.assumptions}
                readOnly={!canEdit}
                saving={update.isPending}
                empty={() => ({ marker: 'A' as const, text: '' })}
                fields={[
                  { key: 'marker', label: 'Art', type: 'select', options: MARKERS, width: 140, placeholder: 'Hinweis' },
                  { key: 'text', label: 'Annahme', type: 'longtext', grow: 1, required: true },
                ]}
                onSave={(assumptions) => update.mutate({ assumptions })}
              />
            </Section>
            <Section title="Risiken">
              <ListEditor
                items={details.risks}
                readOnly={!canEdit}
                saving={update.isPending}
                empty={() => ({ code: `R${details.risks.length + 1}`, title: '', measure: '' })}
                fields={[
                  { key: 'code', label: 'Nr.', width: 60 },
                  { key: 'title', label: 'Risiko', type: 'longtext', grow: 1, required: true },
                  { key: 'measure', label: 'Gegenmaßnahme', type: 'longtext', grow: 1 },
                ]}
                onSave={(risks) => update.mutate({ risks })}
              />
            </Section>
            <Section title="Eskalationsregeln" description="Was geschieht, wenn ein kritischer Termin reißt">
              <ListEditor
                items={details.escalations.map((text) => ({ text }))}
                readOnly={!canEdit}
                saving={update.isPending}
                empty={() => ({ text: '' })}
                fields={[{ key: 'text', label: 'Regel', type: 'longtext', grow: 1, required: true }]}
                onSave={(rows) => update.mutate({ escalations: rows.map((row) => row.text) })}
              />
            </Section>
            <Section title="Wochenraster" description="Wochen des Projekts mit ihrem Kernthema">
              <ListEditor
                items={details.weeks}
                readOnly={!canEdit}
                saving={update.isPending}
                empty={() => ({ label: `W${details.weeks.length + 1}`, kw: null, from: '', to: '', topic: '' })}
                fields={[
                  { key: 'label', label: 'Woche', width: 76, required: true },
                  { key: 'kw', label: 'KW', type: 'number', width: 56 },
                  { key: 'from', label: 'von', type: 'date', width: 136, required: true },
                  { key: 'to', label: 'bis', type: 'date', width: 136, required: true },
                  { key: 'topic', label: 'Kernthema', grow: 1 },
                ]}
                onSave={(weeks) => update.mutate({ weeks })}
              />
            </Section>
          </Stack>
        </SimpleGrid>
      </Stack>
    </div>
  );
}

// --- master data ---------------------------------------------------------

type Draft = Pick<
  ProjectDetails,
  'shortName' | 'reference' | 'description' | 'client' | 'offerDate' | 'offerNet' | 'offerGross' | 'bindingUntil' | 'hardDeadline' | 'planVersion'
>;

function pick(details: ProjectDetails): Draft {
  const { shortName, reference, description, client, offerDate, offerNet, offerGross, bindingUntil, hardDeadline, planVersion } = details;
  return { shortName, reference, description, client, offerDate, offerNet, offerGross, bindingUntil, hardDeadline, planVersion };
}

function MasterData({
  projectId,
  details,
  canEdit,
  saving,
  onSave,
}: {
  projectId: string;
  details: ProjectDetails;
  canEdit: boolean;
  saving: boolean;
  onSave: (body: Partial<ProjectDetails>) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Draft>(pick(details));
  useEffect(() => setDraft(pick(details)), [details]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(pick(details));
  const text = (key: keyof Draft) => ({
    value: (draft[key] as string | null) ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Read now: by the time the updater runs, React has cleared currentTarget
      // (emptying a date field with Backspace blanked the page).
      const value = event.currentTarget.value;
      setDraft((d) => ({ ...d, [key]: value }));
    },
    readOnly: !canEdit,
  });
  const money = (key: 'offerNet' | 'offerGross') => ({
    value: draft[key] ?? '',
    onChange: (next: string | number) => setDraft((d) => ({ ...d, [key]: next === '' ? null : Number(next) })),
    readOnly: !canEdit,
    decimalSeparator: ',',
    thousandSeparator: '.',
    decimalScale: 2,
    suffix: ' €',
    hideControls: true,
  });
  const date = (key: keyof Draft) => ({ ...text(key), type: 'date' });
  const save = (): void => {
    const clean = Object.fromEntries(
      Object.entries(draft).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v]),
    ) as Partial<ProjectDetails>;
    onSave(clean);
  };

  const bindingPassed = details.bindingUntil !== null && details.bindingUntil < new Date().toISOString().slice(0, 10);

  return (
    <Section
      title="Stammdaten"
      description={
        details.bindingUntil ? (
          <>
            Bindefrist {formatDate(details.bindingUntil)}
            {bindingPassed && <Badge ml={6} size="xs" color="red" variant="light">abgelaufen</Badge>}
            {details.offerNet !== null && ` · Angebot ${formatEuro(details.offerNet)} netto`}
          </>
        ) : undefined
      }
      actions={
        canEdit && dirty ? (
          <>
            <Button size="xs" variant="default" onClick={() => setDraft(pick(details))}>Verwerfen</Button>
            <Button size="xs" loading={saving} onClick={save}>Speichern</Button>
          </>
        ) : undefined
      }
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" verticalSpacing="xs">
        <TextInput label="Kurzname" {...text('shortName')} />
        <TextInput label="Zeichen" placeholder="z. B. Angebotszeichen" {...text('reference')} />
      </SimpleGrid>
      <LocationField projectId={projectId} description="Landkreis oder Stadt – dasselbe Feld wie in der Kostenplanung; wird sofort gespeichert." />
      <Textarea label="Auftraggebende" placeholder="Organisation und Anschrift – Personen unter Kontakte" autosize minRows={1} maxRows={4} {...text('client')} />
      <Textarea label="Beschreibung" autosize minRows={2} maxRows={10} {...text('description')} />
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" verticalSpacing="xs">
        <TextInput label="Angebot vom" {...date('offerDate')} />
        <NumberInput label="Angebotssumme netto" {...money('offerNet')} />
        <NumberInput label="Angebotssumme brutto" {...money('offerGross')} />
        <TextInput label="Bindefrist bis" {...date('bindingUntil')} />
        <TextInput label="Fertig spätestens" title="Harte Grenze laut Auftrag" {...date('hardDeadline')} />
        <TextInput label="Planstand" {...text('planVersion')} />
      </SimpleGrid>
    </Section>
  );
}

// --- contacts ------------------------------------------------------------

const EMPTY_CONTACT: ContactInput = {
  code: '', role: '', organisation: '', name: '', function: '', email: '', phone: '', address: '', note: '',
};

function Contacts({ projectId, contacts, canEdit }: { projectId: string; contacts: Contact[]; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [confirm, setConfirm] = useState<Contact | null>(null);
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['projectdata', projectId] });
  const context = useContextMenu<Contact>((contact, close) => {
    const run = (action: () => void) => () => {
      close();
      action();
    };
    const card = [contact.name, contact.function, contact.organisation, contact.address, contact.email, contact.phone].filter(Boolean).join('\n');
    return (
      <>
        <Menu.Label>{contact.name ?? contact.organisation}</Menu.Label>
        {canEdit && <Menu.Item onClick={run(() => setEditing(contact))}>Bearbeiten …</Menu.Item>}
        {contact.email && <Menu.Item component="a" href={`mailto:${contact.email}`} onClick={close}>E-Mail schreiben</Menu.Item>}
        {contact.phone && <Menu.Item component="a" href={`tel:${contact.phone.replace(/\s+/g, '')}`} onClick={close}>Anrufen</Menu.Item>}
        <Menu.Item onClick={run(() => void navigator.clipboard?.writeText(card))}>Kontaktdaten kopieren</Menu.Item>
        {canEdit && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => setConfirm(contact))}>Löschen …</Menu.Item>
          </>
        )}
      </>
    );
  });
  const remove = useMutation({
    mutationFn: (id: string) => projectDataApi.removeContact(projectId, id),
    onSuccess: async () => {
      setConfirm(null);
      await refresh();
    },
  });

  return (
    <Section
      title="Kontakte"
      description={
        <>
          Nur für das Team dieses Projekts sichtbar, einzeln löschbar.{' '}
          <Tooltip
            multiline
            w={340}
            label="Geschäftliche Kontaktdaten zur Durchführung eines Auftrags zu speichern, ist in der Regel zulässig. Halten Sie sie knapp, und löschen Sie sie, wenn sie nicht mehr gebraucht werden. Einzelheiten in docs/DATENSCHUTZ.md."
          >
            <Anchor size="xs" component="span">Datenschutz</Anchor>
          </Tooltip>
        </>
      }
      actions={canEdit ? <Button size="xs" variant="light" onClick={() => setEditing('new')}>+ Kontakt</Button> : undefined}
    >
      {contacts.length === 0 ? (
        <Text size="sm" c="dimmed">Noch keine Kontakte.</Text>
      ) : (
        <Table.ScrollContainer minWidth={520}>
          <Table verticalSpacing={6} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {canEdit && <Table.Th w={36} />}
                <Table.Th w={56}>Kürzel</Table.Th>
                <Table.Th>Name / Organisation</Table.Th>
                <Table.Th>Rolle</Table.Th>
                <Table.Th>Erreichbar</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contacts.map((contact) => (
                <Table.Tr
                  key={contact.id}
                  style={{ cursor: canEdit ? 'pointer' : undefined }}
                  onClick={() => canEdit && setEditing(contact)}
                  onContextMenu={(event) => context.open(event, contact)}
                >
                  {canEdit && (
                    <Table.Td onClick={(event) => event.stopPropagation()}>
                      <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Kontakt löschen" title="Kontakt löschen" onClick={() => setConfirm(contact)}>
                        {'✕'}
                      </ActionIcon>
                    </Table.Td>
                  )}
                  <Table.Td>{contact.code && <Badge variant="light" radius="sm">{contact.code}</Badge>}</Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>{contact.name ?? contact.organisation ?? '—'}</Text>
                    {contact.name && contact.organisation && <Text size="xs" c="dimmed">{contact.organisation}</Text>}
                    {contact.function && <Text size="xs" c="dimmed">{contact.function}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="sm">{contact.role}</Text></Table.Td>
                  <Table.Td onClick={(event) => event.stopPropagation()}>
                    {contact.email && <Anchor size="sm" href={`mailto:${contact.email}`} display="block">{contact.email}</Anchor>}
                    {contact.phone && <Anchor size="sm" href={`tel:${contact.phone.replace(/\s+/g, '')}`} display="block">{contact.phone}</Anchor>}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {context.menu}
      <ContactModal
        projectId={projectId}
        contact={editing}
        onClose={() => setEditing(null)}
        onDone={async () => {
          setEditing(null);
          await refresh();
        }}
      />
      <Modal opened={confirm !== null} onClose={() => setConfirm(null)} title="Kontakt löschen" size="sm">
        <Stack>
          <Text size="sm">
            {confirm?.name ?? confirm?.organisation} wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirm(null)}>Abbrechen</Button>
            <Button color="red" loading={remove.isPending} onClick={() => confirm && remove.mutate(confirm.id)}>Löschen</Button>
          </Group>
        </Stack>
      </Modal>
    </Section>
  );
}

function ContactModal({
  projectId,
  contact,
  onClose,
  onDone,
}: {
  projectId: string;
  contact: Contact | 'new' | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState<ContactInput>(EMPTY_CONTACT);
  const opened = contact !== null;
  useEffect(() => {
    if (contact !== null) setDraft(contact === 'new' ? EMPTY_CONTACT : { ...contact });
  }, [contact]);

  const save = useMutation({
    mutationFn: () => {
      const { id: _id, sortOrder: _sort, ...rest } = draft as Contact;
      void _id; void _sort;
      return contact && contact !== 'new'
        ? projectDataApi.updateContact(projectId, contact.id, rest)
        : projectDataApi.addContact(projectId, rest);
    },
    onSuccess: onDone,
  });
  const field = (key: keyof ContactInput) => ({
    value: (draft[key] as string | null) ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Read now: by the time the updater runs, React has cleared currentTarget
      // (emptying a date field with Backspace blanked the page).
      const value = event.currentTarget.value;
      setDraft((d) => ({ ...d, [key]: value }));
    },
  });
  const ready = Boolean((draft.name ?? '').trim() || (draft.organisation ?? '').trim());

  return (
    <Modal opened={opened} onClose={onClose} title={contact === 'new' ? 'Kontakt anlegen' : 'Kontakt bearbeiten'} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) save.mutate();
        }}
      >
        <Stack gap="sm">
          {save.isError && <Alert color="red">{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" verticalSpacing="xs">
            <TextInput label="Name" data-autofocus {...field('name')} />
            <TextInput label="Organisation" {...field('organisation')} />
            <TextInput label="Funktion" {...field('function')} />
            <TextInput label="Rolle im Projekt" placeholder="Auftraggebende, Nachunternehmen …" {...field('role')} />
            <TextInput label="E-Mail-Adresse" type="email" {...field('email')} />
            <TextInput label="Telefon" type="tel" {...field('phone')} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="sm">
            <TextInput label="Kürzel" placeholder="wie in Verantw." maxLength={12} {...field('code')} />
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput label="Anschrift" {...field('address')} />
            </div>
          </SimpleGrid>
          <Textarea label="Notiz" autosize minRows={1} maxRows={4} {...field('note')} />
          <Text size="xs" c="dimmed">Name oder Organisation genügt. Nur erfassen, was für das Projekt gebraucht wird.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={!ready} loading={save.isPending}>Speichern</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
